const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);
const signed = (diff) => (diff >= 0 ? '+' : '') + fmt(diff);

function DiffCell({ diff, showPlaceholder }) {
  return (
    <td className={`px-3 py-2 text-right font-semibold ${diff >= 0 ? 'text-green-600' : 'text-red-500'}`}>
      {showPlaceholder ? signed(diff) : '-'}
    </td>
  );
}

/**
 * "Projeção de Objetivos" tab — three tables (monthly, quarterly, annual)
 * computed from personal forecasts + real invoicing for the current year.
 */
export default function ProjectionTab({ projections }) {
  const year = new Date().getFullYear();
  return (
    <div className="space-y-6">
      {/* Monthly */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <h3 className="text-sm font-semibold text-[#111827]">Projeção Mensal — {year}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="monthly-projection-table">
            <thead>
              <tr className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
                <th className="px-3 py-2 text-left font-medium">Mês</th>
                <th className="px-3 py-2 text-right font-medium">Previsto</th>
                <th className="px-3 py-2 text-right font-medium">Real</th>
                <th className="px-3 py-2 text-right font-medium">Diferença</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {projections.monthly.map(m => {
                const diff = m.realTotal - m.total;
                const dim = m.total === 0 && m.realTotal === 0;
                return (
                  <tr key={m.mes} className={`hover:bg-[#F9FAFB] ${dim ? 'opacity-40' : ''}`}>
                    <td className="px-3 py-2 font-medium text-[#111827]">{m.label}</td>
                    <td className="px-3 py-2 text-right text-[#0F1829] font-semibold">{fmt(m.total)}</td>
                    <td className="px-3 py-2 text-right text-[#374151]">{fmt(m.realTotal)}</td>
                    <DiffCell diff={diff} showPlaceholder={m.total > 0 || m.realTotal > 0} />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quarterly */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <h3 className="text-sm font-semibold text-[#111827]">Projeção Trimestral</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="quarterly-projection-table">
            <thead>
              <tr className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
                <th className="px-3 py-2 text-left font-medium">Trimestre</th>
                <th className="px-3 py-2 text-right font-medium">Previsto</th>
                <th className="px-3 py-2 text-right font-medium">Real</th>
                <th className="px-3 py-2 text-right font-medium">Diferença</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {projections.quarters.map(q => {
                const diff = q.real - q.previsto;
                return (
                  <tr key={q.label} className="hover:bg-[#F9FAFB]">
                    <td className="px-3 py-2 font-medium text-[#111827]">{q.label}</td>
                    <td className="px-3 py-2 text-right text-[#0F1829] font-semibold">{fmt(q.previsto)}</td>
                    <td className="px-3 py-2 text-right text-[#374151]">{fmt(q.real)}</td>
                    <DiffCell diff={diff} showPlaceholder={q.previsto > 0 || q.real > 0} />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Annual */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-4 sm:p-6" data-testid="annual-projection">
        <h3 className="text-sm font-semibold text-[#111827] mb-4">Projeção Anual — {year}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 text-center">
          <div>
            <p className="text-xs text-[#6B7280] uppercase tracking-wide mb-1">Previsto</p>
            <p className="text-xl sm:text-2xl font-bold text-[#0F1829]">{fmt(projections.annual.previsto)}</p>
          </div>
          <div>
            <p className="text-xs text-[#6B7280] uppercase tracking-wide mb-1">Real</p>
            <p className="text-xl sm:text-2xl font-bold text-[#111827]">{fmt(projections.annual.real)}</p>
          </div>
          <div>
            <p className="text-xs text-[#6B7280] uppercase tracking-wide mb-1">Diferença</p>
            <p className={`text-xl sm:text-2xl font-bold ${projections.annual.real - projections.annual.previsto >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              {signed(projections.annual.real - projections.annual.previsto)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
